"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button } from "../../atoms/button/button";
import { CHEVRON_DOWN_GLYPH_PATH, CHEVRON_DOWN_GLYPH_VIEWBOX } from "../../atoms/chevron-down-glyph";
import { Dropdown, type DropdownOption } from "../../atoms/dropdown/dropdown.client";
import { PINPOINT_GLYPH_PATH, PINPOINT_GLYPH_VIEWBOX } from "../../atoms/pinpoint-glyph";
import { HelperMessage } from "../../atoms/text-field/helper-message";
import { TextField } from "../../atoms/text-field/text-field.client";
import { LINK_POSITION_BODY } from "../../services/analytics";
import { drawableColor, isInvisibleColor } from "../../services/css-color";
import { isValidFullZip } from "../../services/zip/zip-validator";
import { Tooltip } from "../../atoms/tooltip/tooltip.client";
import styles from "./zip-code.module.css";
import { withSafeRender } from "../../atoms/safe-render";

const DEFAULT_TRIGGER_LABEL = "Based on your location";
const DEFAULT_NO_ZIP_LABEL = "City, State ZIP Code";
const DEFAULT_ZIP_LABEL = "ZIP Code";
const DEFAULT_ZIP_INPUT_ERROR_MESSAGE = "Enter a ZIP Code to continue";
const DEFAULT_ZIP_LOOKUP_ERROR_MESSAGE = "Having trouble finding that ZIP Code";
const DEFAULT_COUNTY_LABEL = "County";
const DEFAULT_COUNTY_PLACEHOLDER = "Select your county";
const DEFAULT_COUNTY_SELECTION_ERROR_MESSAGE = "You must select a county to continue";
const DEFAULT_LOOKUP_HANGING_MESSAGE = "Sorry, this search is taking a while...";
const DEFAULT_SUCCESS_MESSAGE = "Update Successful";
const DEFAULT_CTA_LABEL = "Update location";
const DEFAULT_ADD_LOCATION_CTA_LABEL = "Add location";

/* With the picker open, focus and the key event sit on the highlighted <option>, so the select is
   found from the target. `:open` is unknown to older engines and throws there; those have no styled picker. */
function isPickerOpen(target: EventTarget): boolean {
  try {
    const select = target instanceof Element ? target.closest("select") : null;
    return select !== null && select.matches(":open");
  } catch {
    return false;
  }
}

/* Measures before paint on the client, so focus can be handed back before the
   browser clears it from a hidden panel. Falls back to useEffect on the server,
   where useLayoutEffect warns. */
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * ZipCode — the dark-mode ZIP Code: a trigger that discloses a ZIP field, a county
 * picker and a submit CTA, validating ZIP shape then county. It makes no API call;
 * `ZipCodeConnected` wires it to the ZIP services. Open state, field value, errors and
 * the success message are controlled when their prop is given. ECLIPSE-Organisms frame
 * 14976:24783; `design-conflicts/zip-code.mdx` holds the client-boundary and focus arguments.
 */
export interface ZipCodeProps {
  /** Ties the trigger, panel and field ids together; must be unique on the page. */
  id: string;
  /** The trigger's lead-in copy, e.g. "Based on your location". OPEN QUESTION: UX has not confirmed this wording (production reads "Showing details for:"). */
  triggerLabel?: string;
  /** The composed location shown after `triggerLabel`, e.g. "Atlanta, GA 30324"; the host decides whether county appears (OPEN QUESTION: not resolved by UX). Falls back to `zip`, then `noZipLabel`. */
  locationText?: string;
  /** What the trigger reads before any ZIP is known. Defaults to production's "City, State ZIP Code". */
  noZipLabel?: string;
  /** An explanation shown as an info icon beside the trigger. Omitted, no icon renders. */
  tooltipContent?: string;
  /** The info icon's accessible name. Defaults to the tooltip's own. */
  tooltipLabel?: string;
  /** The ZIP currently represented. Seeds the field each time the panel opens, and picks the CTA wording. */
  zip?: string;
  /** Whether the panel starts open. Ignored while `expanded` is set. */
  defaultExpanded?: boolean;
  /** Controls whether the panel is open. Unset, the component keeps its own open state. */
  expanded?: boolean;
  /** Fires with the new expanded state on a manual click. A controlled host applies it through `expanded`. */
  onToggle?: (expanded: boolean) => void;

  /** Label for the ZIP field. Defaults to "ZIP Code". */
  zipLabel?: string;
  /** Controls the ZIP field's value. Unset, the component keeps its own, seeded from `zip`. */
  inputValue?: string;
  /** Shown when the submit is pressed with fewer than 5 digits. */
  zipInputErrorMessage?: string;
  /** Shown instead, when the host reports the ZIP resolved no county (`zipNoResults`). */
  zipLookupErrorMessage?: string;
  /** The host's ZIP lookup returned no matches. A ZIP-shape error outranks it. */
  zipNoResults?: boolean;
  /** Forces the ZIP error on or off. Unset, it follows the component's own check on submit. */
  zipInputError?: boolean;
  /** Fires on every keystroke with the ZIP typed so far, already through production's filter. */
  onZipChange?: (zip: string) => void;
  /** The clear button beside the field was activated. The host empties its own value and selection. */
  onClear?: () => void;

  /** The counties the current ZIP resolved to. Shown only while the field holds a full ZIP. */
  counties?: DropdownOption[];
  /** Label for the county picker. Defaults to "County". */
  countyLabel?: string;
  /** Shown in the county picker while none is selected. Defaults to "Select your county". */
  countyPlaceholder?: string;
  /** Shown when the submit is pressed with no county selected. */
  countySelectionErrorMessage?: string;
  /** Forces the county error on or off. Unset, it follows the component's own check on submit. */
  countySelectionError?: boolean;
  /** The selected county's value, or `""` for none selected. Controlled. */
  selectedCounty?: string;
  /** Fires with the newly selected county's value. */
  onCountySelect?: (value: string) => void;
  /** The county lookup is running: the CTA is disabled and the county field announces it is busy. */
  isCountyLoading?: boolean;

  /** The host's county lookup has been running past the "hanging" threshold. */
  isLookupHanging?: boolean;
  /** Shown while `isLookupHanging` is true. Defaults to the production copy. */
  lookupHangingMessage?: string;
  /** The host's county or location-update API failed. Shown verbatim when set. */
  apiErrorMessage?: string;
  /** The host's submit succeeded: shows `successMessage` in place of any lookup error. The host owns the timing, so a repeat success can restart its own timer. */
  showSuccessMessage?: boolean;
  /** Defaults to "Update Successful". */
  successMessage?: string;

  /** Label for the submit CTA once a ZIP has already been set (`zip` is non-empty). Defaults to "Update location". */
  ctaLabel?: string;
  /** Label for the submit CTA before any ZIP has been set. Defaults to "Add location". */
  addLocationCtaLabel?: string;
  /** Where the CTA reports itself on the page. Defaults to "body". */
  ctaLinkPosition?: string;
  /** With `false` the CTA carries no reporting attributes, for a host that reports the update itself. Defaults to `true`. */
  ctaLinkAttributes?: boolean;
  /** With `true` (the default) the component validates and calls `onSubmit` only when ZIP and county pass. With `false` the host validates, through `zipInputError` / `countySelectionError`. */
  validateOnSubmit?: boolean;
  /** Fires with the typed ZIP and the selected county's value. A county is required whenever the component validates — including when there are none. */
  onSubmit?: (zip: string, county: string) => void;

  /** The ZIP field's and the county dropdown's border. Any OPAQUE CSS colour; one that is not a colour, or is see-through, is ignored. Unset, the Figma colour. */
  fieldStrokeColor?: string;
  /** The same border while the pointer is over it. Set both, so hover stays distinguishable from rest. */
  fieldStrokeHoverColor?: string;

  className?: string;
}

const FIELD_STROKE_PROPERTY = "--zip-code-field-stroke";
const FIELD_STROKE_HOVER_PROPERTY = "--zip-code-field-stroke-hover";

/* The authored colours, through the one check every authored colour field in this tree goes
   through (`drawableColor`, services/css-color.ts). A value it refuses sets nothing, so the
   field keeps its Figma stroke rather than an invalid declaration dropping the border. No
   style attribute at all when neither is set: an empty one is noise on every page. */
const opaqueColor = (value: unknown): string => {
  const colour = drawableColor(value);
  /* ⚠ `drawableColor` KEEPS `transparent` and a zero-alpha colour on purpose — a
     background may legitimately be see-through. A border may not: the field's fill
     is 1.06:1 against the card, so the stroke is the only thing drawing the box and
     an invisible one erases the control. The narrower rule goes here, on top, exactly
     as In-Page Legal applies it to text (services/css-color.ts). */
  return colour === "" || isInvisibleColor(colour) ? "" : colour;
};

function fieldStrokeStyle(stroke: unknown, hover: unknown): React.CSSProperties | undefined {
  const rest = opaqueColor(stroke);
  const over = opaqueColor(hover);
  if (rest === "" && over === "") {
    return undefined;
  }
  /* The cast is how React is handed a custom property at all. */
  return {
    ...(rest !== "" ? { [FIELD_STROKE_PROPERTY]: rest } : {}),
    ...(over !== "" ? { [FIELD_STROKE_HOVER_PROPERTY]: over } : {})
  } as React.CSSProperties;
}

function ZipCodeImpl({
  id,
  triggerLabel = DEFAULT_TRIGGER_LABEL,
  locationText,
  noZipLabel = DEFAULT_NO_ZIP_LABEL,
  tooltipContent,
  tooltipLabel,
  zip = "",
  defaultExpanded = false,
  expanded,
  onToggle,
  zipLabel = DEFAULT_ZIP_LABEL,
  inputValue,
  zipInputErrorMessage = DEFAULT_ZIP_INPUT_ERROR_MESSAGE,
  zipLookupErrorMessage = DEFAULT_ZIP_LOOKUP_ERROR_MESSAGE,
  zipNoResults = false,
  zipInputError,
  onZipChange,
  onClear,
  fieldStrokeColor,
  fieldStrokeHoverColor,
  counties = [],
  countyLabel = DEFAULT_COUNTY_LABEL,
  countyPlaceholder = DEFAULT_COUNTY_PLACEHOLDER,
  countySelectionErrorMessage = DEFAULT_COUNTY_SELECTION_ERROR_MESSAGE,
  countySelectionError,
  selectedCounty = "",
  onCountySelect,
  isCountyLoading = false,
  isLookupHanging = false,
  lookupHangingMessage = DEFAULT_LOOKUP_HANGING_MESSAGE,
  apiErrorMessage,
  showSuccessMessage = false,
  successMessage = DEFAULT_SUCCESS_MESSAGE,
  ctaLabel = DEFAULT_CTA_LABEL,
  addLocationCtaLabel = DEFAULT_ADD_LOCATION_CTA_LABEL,
  ctaLinkPosition = LINK_POSITION_BODY,
  ctaLinkAttributes = true,
  validateOnSubmit = true,
  onSubmit,
  className
}: ZipCodeProps) {
  const [ownExpanded, setOwnExpanded] = useState(defaultExpanded);
  const [ownInput, setOwnInput] = useState(zip);
  const [ownZipShapeError, setOwnZipShapeError] = useState(false);
  const [ownCountyMissingError, setOwnCountyMissingError] = useState(false);
  const isExpanded = expanded ?? ownExpanded;
  const inputZip = inputValue ?? ownInput;
  const panelId = `${id}-panel`;
  const zipFieldId = `${id}-zip`;
  const countyFieldId = `${id}-county`;
  const ctaId = `${id}-cta`;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);

  // Set when a click opens the panel, so only a user open (not a host-driven one) moves focus.
  const openedByUserRef = useRef(false);
  const wasExpandedRef = useRef(isExpanded);

  // Re-seed an uncontrolled field from the committed ZIP whenever the panel opens.
  useEffect(() => {
    if (isExpanded && inputValue === undefined) {
      setOwnInput(zip);
    }
  }, [isExpanded, zip, inputValue]);

  useEffect(() => {
    if (isExpanded && openedByUserRef.current) {
      zipInputRef.current?.focus();
    }
    openedByUserRef.current = false;
  }, [isExpanded]);

  /* Focus must not be dropped when the panel closes under it — the host's
     success timer does that, not a click. Handed back to the trigger only if
     focus was inside the panel; a reader who has moved on is left alone. */
  useIsomorphicLayoutEffect(() => {
    if (wasExpandedRef.current && !isExpanded) {
      const active = document.activeElement;
      if (panelRef.current && active && panelRef.current.contains(active)) {
        triggerRef.current?.focus();
      }
    }
    wasExpandedRef.current = isExpanded;
  }, [isExpanded]);

  // Button writes no destination on its <button> form, so it is set after mount and withheld while the CTA is disabled.
  useEffect(() => {
    const cta = document.getElementById(ctaId);
    if (!cta) {
      return;
    }
    if (isCountyLoading || !ctaLinkAttributes) {
      cta.removeAttribute("data-linkdestination");
      return;
    }
    cta.setAttribute("data-linkdestination", window.location.href);
  }, [ctaId, ctaLinkAttributes, isCountyLoading, isExpanded]);

  /* A disabled button cannot hold focus, so the browser would drop it to the
     page. When the lookup disables the CTA under the reader, focus moves to the
     ZIP field — the control that is being waited on. */
  useIsomorphicLayoutEffect(() => {
    if (isCountyLoading && document.activeElement?.id === ctaId) {
      zipInputRef.current?.focus();
    }
  }, [isCountyLoading, ctaId]);

  /* Escape closes an open panel; a key a child already handled (the tooltip's own
     Escape) is left alone. */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Escape" || event.defaultPrevented || !isExpanded) {
      return;
    }
    // An open county picker takes this Escape itself; the panel stays open.
    if (isPickerOpen(event.target)) {
      return;
    }
    if (expanded === undefined) {
      setOwnExpanded(false);
    }
    // Focus follows the panel actually closing (the layout effect above), so a host that
    // ignores onToggle(false) keeps it where it is.
    onToggle?.(false);
  };

  const toggle = () => {
    const next = !isExpanded;
    if (next) {
      openedByUserRef.current = true;
    }
    if (expanded === undefined) {
      setOwnExpanded(next);
    }
    onToggle?.(next);
  };

  const handleZipChange = (value: string) => {
    if (inputValue === undefined) {
      setOwnInput(value);
    }
    setOwnZipShapeError(false);
    onZipChange?.(value);
  };

  const handleClear = () => {
    if (inputValue === undefined) {
      setOwnInput("");
    }
    setOwnZipShapeError(false);
    onClear?.();
  };

  const handleCountySelect = (value: string) => {
    setOwnCountyMissingError(false);
    onCountySelect?.(value);
  };

  const handleSubmit = () => {
    if (!validateOnSubmit) {
      onSubmit?.(inputZip, selectedCounty);
      return;
    }
    if (!isValidFullZip(inputZip)) {
      setOwnZipShapeError(true);
      return;
    }
    // Also when there are no counties: no results and API errors read as a missing county.
    if (!selectedCounty) {
      setOwnCountyMissingError(true);
      return;
    }
    setOwnZipShapeError(false);
    setOwnCountyMissingError(false);
    onSubmit?.(inputZip, selectedCounty);
  };

  // The ZIP-shape error always outranks "no results": a reader who has since
  // edited the ZIP to an invalid shape needs THAT problem, not a stale lookup
  // miss. Matches production's zipCodeMessage order.
  const shapeError = zipInputError ?? ownZipShapeError;
  const zipError = shapeError || zipNoResults;
  const zipErrorMessage = shapeError ? zipInputErrorMessage : zipLookupErrorMessage;
  const countyError = countySelectionError ?? ownCountyMissingError;

  // Options only for a full ZIP; a single choice is shown but not openable.
  const visibleCounties = inputZip.length === 5 ? counties : [];

  const resolvedLocationText = locationText || (zip ? zip : noZipLabel);
  const cta = zip ? ctaLabel : addLocationCtaLabel;
  /* Success hides any lookup error, as production does; an API error outranks
     "taking a while". ⚠ ONLY THE HANGING MESSAGE REACHES HERE AS PLAIN TEXT — the
     API error is drawn as an error-toned message below, so it must not also be
     spilled into this string or it renders twice. */
  const statusMessage = showSuccessMessage || apiErrorMessage ?
    "" :
    (isLookupHanging ? lookupHangingMessage : "");

  return (
    <div
      className={[styles.root, isExpanded ? styles.rootExpanded : null, className].filter(Boolean).join(" ")}
      onKeyDown={handleKeyDown}
      style={fieldStrokeStyle(fieldStrokeColor, fieldStrokeHoverColor)}
    >
      <div className={styles.triggerRow}>
        <button
          aria-controls={panelId}
          aria-expanded={isExpanded}
          className={styles.trigger}
          id={id}
          onClick={toggle}
          ref={triggerRef}
          type="button"
        >
          <span className={styles.triggerLabel}>{triggerLabel}</span>
          <span className={styles.locationPicker}>
            <svg aria-hidden="true" className={styles.pinIcon} focusable="false" viewBox={PINPOINT_GLYPH_VIEWBOX}>
              <path d={PINPOINT_GLYPH_PATH} fill="currentColor" />
            </svg>
            <span className={styles.locationText}>{resolvedLocationText}</span>
            <svg
              aria-hidden="true"
              className={[styles.chevron, isExpanded ? styles.chevronExpanded : null]
                .filter(Boolean)
                .join(" ")}
              focusable="false"
              viewBox={CHEVRON_DOWN_GLYPH_VIEWBOX}
            >
              <path d={CHEVRON_DOWN_GLYPH_PATH} fill="currentColor" />
            </svg>
          </span>
        </button>
        {/* A sibling of the trigger, not inside it: a button cannot hold a button. */}
        {tooltipContent && <Tooltip content={tooltipContent} label={tooltipLabel} />}
      </div>

      {/* Always mounted (argument: design-conflicts/zip-code.mdx), so `aria-controls`
          always resolves. `hidden` takes the subtree out of the tab order and the
          accessibility tree; the module restates `display: none` because an author
          `display` rule beats the user-agent `[hidden]` rule on origin. */}
      <div className={styles.panel} hidden={!isExpanded} id={panelId} ref={panelRef}>
        {/* The row is ZIP, county, CTA in focus order. The status is NOT inside it:
            an empty live region as a flex item of the wrapping row corrupts its
            intrinsic width and wraps the CTA. */}
        <div className={styles.fields}>
          <TextField
            error={zipError}
            errorMessage={zipErrorMessage}
            id={zipFieldId}
            label={zipLabel}
            onClear={handleClear}
            onValueChange={handleZipChange}
            ref={zipInputRef}
            autoComplete="postal-code"
            inputFilter="zip"
            value={inputZip}
          />

          <Dropdown
            /* ⚠ NO OPTIONS MEANS NOT OPERABLE. Left enabled, this is a native
               `<select>` with nothing in it, and where the browser draws the list
               itself (`appearance: base-select` — Chrome/Edge 135+, see the county
               list section of design-conflicts) opening it paints an EMPTY menu
               card: a control that responds, shows nothing, and explains nothing.
               FOUR states reach it, and in all four there is nothing to choose:
               before a five-digit ZIP; while the lookup is in flight (the session
               clears `counties` first, so this is `disabled` and `aria-busy`
               together); when the lookup returns no results; and when it fails.
               `locked` is the one-option case and is not this: it swaps in
               read-only text for a decision already made.
               ⚠ `disabled` also cancels `focusOnError` below — a disabled select
               cannot take focus. `Dropdown` compensates by announcing the message
               instead; the two must not be separated. See conflicts entry 16. */
            disabled={visibleCounties.length === 0}
            error={countyError}
            errorMessage={countySelectionErrorMessage}
            focusOnError
            id={countyFieldId}
            label={countyLabel}
            loading={isCountyLoading}
            locked={visibleCounties.length === 1 && selectedCounty === visibleCounties[0].value}
            onValueChange={handleCountySelect}
            options={visibleCounties}
            placeholder={countyPlaceholder}
            value={selectedCounty}
          />

          <Button
            className={styles.cta}
            disabled={isCountyLoading}
            id={ctaId}
            {...(ctaLinkAttributes ? {
              linkAttributes: true as const,
              linkPosition: ctaLinkPosition
            } : null)}
            onClick={handleSubmit}
            size="medium"
            type="button"
            variant="secondary"
          >
            {cta}
          </Button>
        </div>

        {/* Mounted even when empty, so it is registered before its first message.
            It sits inside the hidden panel while closed, where it is not
            exposed at all: that is fine, since every message is for an open
            panel. Slow-lookup, API failure and success route through it (AC4). */}
        <div className={styles.status} role="status">
          {showSuccessMessage ? (
            <HelperMessage id={`${id}-success`} tone="success">
              {successMessage}
            </HelperMessage>
          ) : apiErrorMessage ? (
            /* ⚠ `announce={false}` IS LOAD-BEARING: `tone="error"` otherwise puts
               `role="alert"` on this <p>, inside the `role="status"` region that is
               already announcing it — the double-announce AMP121 reported for the
               success message (design-conflicts). The region announces; this only
               draws. */
            <HelperMessage announce={false} id={`${id}-api-error`} tone="error">
              {apiErrorMessage}
            </HelperMessage>
          ) : statusMessage}
        </div>
      </div>
    </div>
  );
}
export const ZipCode = withSafeRender(ZipCodeImpl, "ZipCode");

export default ZipCode;
